import {
  LeadStatus,
  type ReputationOverviewDto,
  ReputationInsightSeverity,
  type ReputationSummaryDto,
} from '@up2stay/up2promo-api-client';

import { locationsNeedsAttentionHref } from '@/core/components/locations/locations-list-filter-url';
import type { ReputationConnectionSetupBannerVariant } from '@/core/hooks/use-reputation-connection-setup';
import { shellPaths } from '@/core/router/app-paths';

import {
  DO_NEXT_LIMIT,
  doNextRank,
  formatInsightText,
  insightHref,
} from './dashboard-status-helpers';
import type {
  DashboardDoNextItem,
  DashboardStatusLabels,
} from './dashboard-status-types';

/** `{count}` label with a singular variant ("1 new lead", not "1 new leads"). */
function countLabel(count: number, many: string, one: string): string {
  return count === 1 ? one : many.replace('{count}', String(count));
}

export function buildDashboardDoNextItems(input: {
  reputationEnabled: boolean;
  overview: ReputationOverviewDto | undefined;
  failedSyncLocationIds: Set<string>;
  canConfigureReputation: boolean;
  showConnectionBanner: boolean;
  bannerVariant: ReputationConnectionSetupBannerVariant | null;
  needsActionCount: number;
  newLeadsCount: number;
  feedbackEnabled: boolean;
  unrepliedFeedbackCount: number;
  canManageLocations: boolean;
  locationsPending: boolean;
  locationsCount: number;
  profileLoading: boolean;
  profileIsNull: boolean;
  canManageProducts: boolean;
  productsPending: boolean;
  totalProducts: number;
  productsError: boolean;
  summary: ReputationSummaryDto | undefined;
  insightTemplates: Record<string, string>;
  labels: DashboardStatusLabels;
}): DashboardDoNextItem[] {
  const {
    reputationEnabled,
    overview,
    failedSyncLocationIds,
    canConfigureReputation,
    showConnectionBanner,
    bannerVariant,
    needsActionCount,
    newLeadsCount,
    feedbackEnabled,
    unrepliedFeedbackCount,
    canManageLocations,
    locationsPending,
    locationsCount,
    profileLoading,
    profileIsNull,
    canManageProducts,
    productsPending,
    totalProducts,
    productsError,
    summary,
    insightTemplates,
    labels,
  } = input;

  const items: DashboardDoNextItem[] = [];

  if (reputationEnabled && overview) {
    const critical = overview.unrepliedCriticalCount ?? 0;
    if (critical > 0) {
      items.push({
        id: 'critical-reviews',
        severity: 'critical',
        title: countLabel(
          critical,
          labels.doNextCriticalReviews,
          labels.doNextCriticalReviewsOne,
        ),
        subtitle: labels.doNextCriticalReviewsDesc,
        count: critical,
        href: shellPaths.reputationInbox,
      });
    }
  }

  if (failedSyncLocationIds.size > 0) {
    items.push({
      id: 'sync-failed',
      severity: 'critical',
      title: countLabel(
        failedSyncLocationIds.size,
        labels.doNextSyncFailed,
        labels.doNextSyncFailedOne,
      ),
      subtitle: labels.doNextSyncFailedDesc,
      count: failedSyncLocationIds.size,
      href: locationsNeedsAttentionHref(shellPaths.moreWorkspaceLocations),
    });
  }

  const withoutReviews = overview?.locationsWithoutReviews ?? [];
  if (reputationEnabled && withoutReviews.length > 0) {
    const first = withoutReviews[0]!;
    items.push({
      id: 'zero-reviews',
      severity: 'neutral',
      title:
        withoutReviews.length === 1
          ? labels.doNextZeroReviewsOne.replace('{name}', first.locationName)
          : labels.doNextZeroReviews.replace(
              '{count}',
              String(withoutReviews.length),
            ),
      subtitle: labels.doNextZeroReviewsDesc,
      count: withoutReviews.length,
      href:
        withoutReviews.length === 1
          ? shellPaths.moreWorkspaceLocationDetail(first.locationId)
          : shellPaths.reputationDashboard,
    });
  }

  if (
    canConfigureReputation &&
    reputationEnabled &&
    showConnectionBanner &&
    bannerVariant
  ) {
    if (bannerVariant === 'reconnect_google') {
      items.push({
        id: 'setup-reconnect',
        severity: 'critical',
        title: labels.setupReconnectGoogle,
        subtitle: labels.setupReconnectGoogleDesc,
        count: 1,
        href: shellPaths.reputationSetup,
      });
    } else if (bannerVariant === 'connect_google') {
      items.push({
        id: 'setup-connect',
        severity: 'neutral',
        title: labels.setupConnectGoogle,
        subtitle: labels.setupConnectGoogleDesc,
        count: 1,
        href: shellPaths.reputationSetup,
      });
    } else if (bannerVariant === 'link_locations') {
      items.push({
        id: 'setup-link',
        severity: 'neutral',
        title: countLabel(
          needsActionCount,
          labels.setupLinkLocations,
          labels.setupLinkLocationsOne,
        ),
        subtitle: labels.setupLinkLocationsDesc,
        count: Math.max(1, needsActionCount),
        href: shellPaths.reputationSetup,
      });
    }
  }

  if (newLeadsCount > 0) {
    items.push({
      id: 'new-leads',
      severity: 'info',
      title: countLabel(
        newLeadsCount,
        labels.doNextNewLeads,
        labels.doNextNewLeadsOne,
      ),
      subtitle: labels.doNextNewLeadsDesc,
      count: newLeadsCount,
      href: `${shellPaths.leads}?status=${LeadStatus.NEW}`,
    });
  }

  if (feedbackEnabled && unrepliedFeedbackCount > 0) {
    items.push({
      id: 'open-feedback',
      severity: 'warning',
      title: labels.doNextOpenFeedback.replace(
        '{count}',
        String(unrepliedFeedbackCount),
      ),
      subtitle: labels.doNextOpenFeedbackDesc,
      count: unrepliedFeedbackCount,
      href: shellPaths.feedbackActivity,
    });
  }

  if (canManageLocations && !locationsPending && locationsCount === 0) {
    items.push({
      id: 'add-location',
      severity: 'warning',
      title: labels.doNextAddLocation,
      subtitle: labels.doNextAddLocationDesc,
      count: 1,
      href: shellPaths.moreWorkspaceLocationCreate,
    });
  }

  if (!profileLoading && profileIsNull) {
    items.push({
      id: 'create-profile',
      severity: 'info',
      title: labels.doNextCreateProfile,
      subtitle: labels.doNextCreateProfileDesc,
      count: 1,
      href: shellPaths.profiles,
    });
  }

  if (
    canManageProducts &&
    !productsPending &&
    totalProducts === 0 &&
    !productsError
  ) {
    items.push({
      id: 'add-product',
      severity: 'info',
      title: labels.doNextAddProduct,
      subtitle: labels.doNextAddProductDesc,
      count: 1,
      href: shellPaths.moreProductsSetup,
    });
  }

  // Soft hygiene last — never above hard interrupts.
  if (reputationEnabled && overview) {
    const critical = overview.unrepliedCriticalCount ?? 0;
    for (const insight of overview.insights) {
      if (insight.severity === ReputationInsightSeverity.SUCCESS) continue;
      if (insight.key === 'unrepliedCritical' && critical > 0) continue;
      if (insight.key === 'noReviews' && withoutReviews.length > 0) continue;
      if (items.some((i) => i.id === `insight-${insight.key}`)) continue;
      items.push({
        id: `insight-${insight.key}`,
        severity:
          insight.severity === ReputationInsightSeverity.WARNING
            ? 'warning'
            : 'info',
        title: formatInsightText(insightTemplates, insight),
        href: insightHref(insight),
      });
    }
  }

  if (
    reputationEnabled &&
    summary?.status === 'ready' &&
    summary.actionItems?.length
  ) {
    for (const [index, action] of summary.actionItems.entries()) {
      items.push({
        id: `summary-${index}`,
        severity: 'info',
        title: action,
        href: shellPaths.reputationDashboard,
      });
    }
  }

  items.sort((a, b) => doNextRank(a.id) - doNextRank(b.id));

  const seen = new Set<string>();
  const unique: DashboardDoNextItem[] = [];
  for (const item of items) {
    const key = `${item.href}:${item.title}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(item);
    if (unique.length >= DO_NEXT_LIMIT) break;
  }
  return unique;
}
